import {
  AHIcon,
  AHText,
  AHWidgetTitleText,
  AHWidgetTitleWrapper,
  Box,
  TouchableBox,
  useLayoutContext,
  useScaleFactor,
} from "arkh";
import { Image } from "expo-image";
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { graphql, useFragment } from "react-relay";
import { BaseWidgetProps } from "../BaseProps";
import { useSetWidgetFocus } from "../useSetFocus";
import { WidgetTable_cell$key } from "./__generated__/WidgetTable_cell.graphql";
import { WidgetTable_row$key } from "./__generated__/WidgetTable_row.graphql";
import {
  WidgetTable_section$data,
  WidgetTable_section$key,
} from "./__generated__/WidgetTable_section.graphql";
import { WidgetTable_sheet$key } from "./__generated__/WidgetTable_sheet.graphql";
import { WidgetTable_table$key } from "./__generated__/WidgetTable_table.graphql";
import { useDebouncedCallback } from "use-debounce";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";
import { useInterval } from "../../useInterval";

type Props = {
  widget: WidgetTable_table$key;
  detailId?: string;
  ref?: React.RefObject<WidgetTableRef | null>;
} & BaseWidgetProps;

type SortColumn = {
  columnIndex: number;
  order: "asc" | "desc";
};

export type WidgetTableRef = {
  scrollTo: (itemId: string) => void;
};

export const WidgetTable = (props: Props) => {
  const widget = useFragment(
    graphql`
      fragment WidgetTable_table on WidgetTable {
        id
        title
        dataSource
        sheets {
          title
          ...WidgetTable_sheet
        }
      }
    `,
    props.widget
  );

  const title = useMemo(() => {
    if (widget.dataSource.startsWith("POWER_BI")) {
      return (
        <Image
          style={{ flex: 1 }}
          contentFit="scale-down"
          contentPosition="left center"
          source={require("../WidgetKPI/PowerBILogo.png")}
        />
      );
    } else {
      return <AHWidgetTitleText>{widget.title}</AHWidgetTitleText>;
    }
  }, [widget.dataSource, widget.title]);

  const setWidgetFocus = useSetWidgetFocus(widget.id, props.storecastId);

  const [focusSheetIndex, focusSort] = useMemo(() => {
    if (!props.detailId) return [undefined, undefined];

    const [sheetIndex, ...sortParts] = props.detailId.split(":");

    const focusSort =
      sortParts.length === 0
        ? []
        : sortParts
            .map((sortColumn) => {
              const [index, order] = sortColumn.split(",");

              return {
                columnIndex: parseInt(index),
                order: order as "asc" | "desc",
              };
            })
            .filter((sortPart) => !Number.isNaN(sortPart.columnIndex));

    return [parseInt(sheetIndex), focusSort];
  }, [props.detailId]);

  const [sort, setSort] = useState<SortColumn[]>([]);

  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  useInterval(
    () => {
      setActiveSheetIndex((activeSheetIndex + 1) % widget.sheets.length);
    },
    props.idiom === "STATIC" ? 12000 : null
  );

  const setFocus = useCallback(
    (sheetIndex: number, sort: SortColumn[]) => {
      setWidgetFocus(
        `${sheetIndex ?? 0}:${sort.map((columnSort) => `${columnSort.columnIndex},${columnSort.order}`).join(":")}`
      );
    },
    [setWidgetFocus]
  );

  const toggleSort = useCallback(
    (columnIndex: number) => {
      const newSort =
        sort[0]?.columnIndex === columnIndex
          ? [
              {
                columnIndex,
                order:
                  sort[0].order === "asc"
                    ? ("desc" as const)
                    : ("asc" as const),
              },
              ...sort.slice(1),
            ]
          : [
              {
                columnIndex,
                order: "asc" as const,
              },
              ...sort.filter(
                (sortColumn) => sortColumn.columnIndex !== columnIndex
              ),
            ];

      setSort(newSort);

      if (props.isFocused) {
        setFocus(activeSheetIndex, newSort);
      }
    },
    [sort, setSort, props.isFocused, activeSheetIndex, setFocus]
  );

  const setScroll = useSetScrollFocusMutation(props.storecastId, widget.id);

  const layout = useLayoutContext();

  return (
    <TouchableBox
      disabled={props.mode !== "FOCUS" || props.isFocused}
      style={{ flex: 1 }}
      color="surface"
      onPress={
        props.interactive === false
          ? undefined
          : () => setFocus(activeSheetIndex, sort)
      }
    >
      <Box color="surface" style={{ flex: 1 }}>
        <AHWidgetTitleWrapper
          style={Platform.OS !== "web" ? { flex: 0 } : undefined}
        >
          {title}
        </AHWidgetTitleWrapper>
        <FadeTransition
          activeIndex={focusSheetIndex ?? activeSheetIndex}
          items={widget.sheets}
          render={(sheet, i) => (
            <Sheet
              ref={
                i === (focusSheetIndex ?? activeSheetIndex)
                  ? props.ref
                  : undefined
              }
              sheet={sheet}
              sort={focusSort ?? sort}
              toggleSort={toggleSort}
              setScroll={
                props.isFocused && i === (focusSheetIndex ?? activeSheetIndex)
                  ? (fraction) => {
                      setScroll(`${fraction}`);
                    }
                  : undefined
              }
            />
          )}
        />
      </Box>
      <Box
        style={{ borderTopWidth: 1 }}
        color="surface"
        borderColor="border-strong"
        spaceVertical="small"
      >
        <FlatList
          data={widget.sheets}
          contentContainerStyle={{
            paddingLeft: layout.space.medium,
            paddingRight: layout.space.small + layout.space.xsmall,
          }}
          horizontal
          showsHorizontalScrollIndicator={false}
          renderItem={({ item: sheet, index: i }) => (
            <TouchableBox
              key={i}
              onPress={() => {
                setActiveSheetIndex(i);
                if (props.isFocused) {
                  setFocus(i, sort);
                }
              }}
              spaceRight="xsmall"
            >
              <Box
                spaceHorizontal="medium"
                borderColor={
                  i === (focusSheetIndex ?? activeSheetIndex)
                    ? "primary"
                    : "border-primary"
                }
                style={{
                  borderWidth: 1,
                  height: layout.circle.medium,
                  borderRadius: layout.circle.medium / 2,
                  alignItems: "center",
                  justifyContent: "center",
                }}
                color={
                  i === (focusSheetIndex ?? activeSheetIndex)
                    ? "primary"
                    : "surface"
                }
              >
                <AHText
                  type="subtitle"
                  color={
                    i === (focusSheetIndex ?? activeSheetIndex)
                      ? "text-inverted"
                      : "text-washed"
                  }
                >
                  {sheet.title}
                </AHText>
              </Box>
            </TouchableBox>
          )}
        />
      </Box>
    </TouchableBox>
  );
};

const FadeTransitionMobile = <T extends any>(props: {
  activeIndex: number;
  items: readonly T[];
  render: (item: T, index: number) => ReactNode;
}) => {
  const [aIndex, setAIndex] = useState(props.activeIndex);
  const [bIndex, setBIndex] = useState(props.activeIndex);
  const [bVisible, setBVisible] = useState(false);
  const bOpacity = useSharedValue(0);

  useEffect(() => {
    if (
      bVisible ? props.activeIndex !== bIndex : props.activeIndex !== aIndex
    ) {
      if (bVisible) {
        setAIndex(props.activeIndex);
        setBVisible(false);
        bOpacity.value = withTiming(0, { duration: 500 });
      } else {
        setBIndex(props.activeIndex);
        setBVisible(true);
        bOpacity.value = withTiming(1, { duration: 500 });
      }
    }
  }, [
    props.activeIndex,
    setAIndex,
    aIndex,
    setBIndex,
    bIndex,
    bVisible,
    setBVisible,
    bOpacity,
  ]);

  const bHidden = useDerivedValue(() => bOpacity.value === 0);

  const bStyle = useAnimatedStyle(() => {
    return {
      display: bHidden.value ? "none" : undefined,
      opacity: bOpacity.value,
    };
  }, [bVisible]);

  return (
    <View style={{ flex: 1, position: "relative" }}>
      <Animated.View style={StyleSheet.absoluteFill}>
        {aIndex in props.items && props.render(props.items[aIndex], aIndex)}
      </Animated.View>
      <Animated.View style={[StyleSheet.absoluteFillObject, bStyle]}>
        {bIndex in props.items && props.render(props.items[bIndex], bIndex)}
      </Animated.View>
    </View>
  );
};

const FadeInElemWeb = ({ children }: PropsWithChildren) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => setVisible(true), []);

  return (
    <div
      style={{
        ...StyleSheet.absoluteFillObject,
        display: "flex",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.5s",
        backgroundColor: "#ffffff",
      }}
    >
      {children}
    </div>
  );
};

const FadeTransitionWeb = <T extends any>(props: {
  activeIndex: number;
  items: readonly T[];
  render: (item: T, index: number) => ReactNode;
}) => {
  const prevIndex = useRef<number | undefined>(undefined);
  const currentIndex = useRef<number | undefined>(undefined);

  if (
    currentIndex.current === undefined ||
    currentIndex.current !== props.activeIndex
  ) {
    prevIndex.current = currentIndex.current;
    currentIndex.current = props.activeIndex;
  }

  return (
    <View style={{ flex: 1, position: "relative" }}>
      {prevIndex.current !== undefined && (
        <div style={{ ...StyleSheet.absoluteFillObject }}>
          {prevIndex.current in props.items &&
            props.render(props.items[prevIndex.current], prevIndex.current)}
        </div>
      )}
      <FadeInElemWeb key={props.activeIndex}>
        {props.activeIndex in props.items &&
          props.render(props.items[props.activeIndex], props.activeIndex)}
      </FadeInElemWeb>
    </View>
  );
};

const FadeTransition =
  Platform.OS === "web" ? FadeTransitionWeb : FadeTransitionMobile;

const Sheet = (props: {
  sheet: WidgetTable_sheet$key;
  sort: SortColumn[];
  toggleSort: (columnIndex: number) => void;
  setScroll?: (fraction: number) => void;
  ref?: React.RefObject<WidgetTableRef | null>;
}) => {
  const sheet = useFragment(
    graphql`
      fragment WidgetTable_sheet on WidgetTableSheet {
        title
        columns {
          label
        }
        sections {
          ...WidgetTable_section
        }
      }
    `,
    props.sheet
  );

  const sortFunction = useCallback(
    (
      rowA: WidgetTable_section$data["rows"][number],
      rowB: WidgetTable_section$data["rows"][number]
    ) => {
      for (const columnSort of props.sort) {
        const a =
          rowA.cells[columnSort.columnIndex]?.sortValue ??
          rowA.cells[columnSort.columnIndex]?.value;
        const b =
          rowB.cells[columnSort.columnIndex]?.sortValue ??
          rowB.cells[columnSort.columnIndex]?.value;

        const order = columnSort.order === "asc" ? 1 : -1;

        if (typeof a === "number" && typeof b === "number") {
          return (a - b) * order;
        } else if (a === null) {
          return 1;
        } else if (b === null) {
          return -1;
        } else {
          return a.toString().localeCompare(b.toString(), "sv-SE") * order;
        }
      }

      return 0;
    },
    [props.sort]
  );

  const [height, setHeight] = useState<null | number>(null);
  const [innerHeight, setInnerHeight] = useState<null | number>(null);

  const scrollViewRef = useRef<ScrollView>(null);
  useImperativeHandle(props.ref, () => ({
    scrollTo(itemId) {
      if (height !== null && innerHeight !== null) {
        const y = parseFloat(itemId);
        if (!Number.isNaN(y)) {
          scrollViewRef.current?.scrollTo(y * (innerHeight - height));
        }
      }
    },
  }));

  const debouncedOnScroll = useDebouncedCallback(
    (fraction: number) => {
      props.setScroll?.(fraction);
    },
    200,
    {
      leading: true,
      maxWait: 1000,
    }
  );

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const targetOffset = event.nativeEvent.targetContentOffset
        ? event.nativeEvent.targetContentOffset.y
        : event.nativeEvent.contentOffset.y;

      const height =
        event.nativeEvent.contentSize.height -
        event.nativeEvent.layoutMeasurement.height;

      const fraction = targetOffset / (height ?? 1);

      debouncedOnScroll(fraction);
    },
    [debouncedOnScroll]
  );

  return (
    <Box spaceTop="small" style={{ flex: 1 }}>
      <ScrollView
        ref={scrollViewRef}
        onScroll={props.setScroll ? onScroll : undefined}
        onLayout={(e) => setHeight(e.nativeEvent.layout.height)}
      >
        <View
          style={{ flexDirection: "row" }}
          onLayout={(e) => setInnerHeight(e.nativeEvent.layout.height)}
        >
          {sheet.columns.map(({ label }, columnIndex) => (
            <View
              key={columnIndex}
              style={{ flexDirection: "column", flexGrow: 1 }}
            >
              <CellWrapper
                firstColumn={columnIndex === 0}
                lastColumn={columnIndex === sheet.columns.length - 1}
                onPress={() => props.toggleSort(columnIndex)}
              >
                <AHText type="subtitle">{label}</AHText>
                <SortIcon
                  order={
                    props.sort.find(
                      (sortColumn) => sortColumn.columnIndex === columnIndex
                    )?.order
                  }
                  first={props.sort[0]?.columnIndex === columnIndex}
                />
              </CellWrapper>
              {sheet.sections.map((section, i) => (
                <Section
                  key={i}
                  section={section}
                  columnIndex={columnIndex}
                  firstColumn={columnIndex === 0}
                  lastColumn={columnIndex === sheet.columns.length - 1}
                  sortFunction={
                    props.sort.length === 0 ? undefined : sortFunction
                  }
                />
              ))}
            </View>
          ))}
        </View>
      </ScrollView>
    </Box>
  );
};

const SortIcon = (props: { order?: "asc" | "desc"; first?: boolean }) => {
  if (props.order === undefined) return null;

  return (
    <AHIcon
      type={props.order === "asc" ? "chevron-up" : "chevron-down"}
      color={props.first ? "status-active" : "status-inactive"}
    />
  );
};

const Section = (props: {
  section: WidgetTable_section$key;
  columnIndex: number;
  firstColumn?: boolean;
  lastColumn?: boolean;
  sortFunction?: (
    a: WidgetTable_section$data["rows"][number],
    b: WidgetTable_section$data["rows"][number]
  ) => number;
}) => {
  const section = useFragment(
    graphql`
      fragment WidgetTable_section on WidgetTableSection {
        headerRow {
          ...WidgetTable_row
        }
        rows {
          cells {
            value
            sortValue
          }
          ...WidgetTable_row
        }
      }
    `,
    props.section
  );

  const sortedRows = useMemo(() => {
    if (!props.sortFunction) return section.rows;

    return [...section.rows].sort(props.sortFunction);
  }, [section.rows, props.sortFunction]);

  return (
    <>
      {section.headerRow && (
        <RowCell
          row={section.headerRow}
          columnIndex={props.columnIndex}
          firstColumn={props.firstColumn}
          lastColumn={props.lastColumn}
          header
        />
      )}
      {sortedRows.map((row, i) => (
        <RowCell
          key={i}
          row={row}
          columnIndex={props.columnIndex}
          firstColumn={props.firstColumn}
          lastColumn={props.lastColumn}
        />
      ))}
    </>
  );
};

const CellWrapper = (
  props: PropsWithChildren<{
    firstColumn?: boolean;
    lastColumn?: boolean;
    header?: boolean;
    onPress?: () => void;
  }>
) => {
  const sf = useScaleFactor();

  const content = (
    <Box
      style={{
        height: 32 * sf,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 4 * sf,
        ...(props.header
          ? null
          : {
              borderTopWidth: 1,
            }),
      }}
      borderColor={"border-primary"}
      color={props.header ? "surface-background-light" : "surface"}
      spaceLeft={props.firstColumn ? "medium" : "small"}
      spaceRight={props.lastColumn ? "medium" : "small"}
    >
      {props.children}
    </Box>
  );

  if (props.onPress) {
    return <TouchableBox onPress={props.onPress}>{content}</TouchableBox>;
  }

  return content;
};

const RowCell = (props: {
  row: WidgetTable_row$key;
  columnIndex: number;
  firstColumn?: boolean;
  lastColumn?: boolean;
  header?: boolean;
}) => {
  const row = useFragment(
    graphql`
      fragment WidgetTable_row on WidgetTableRow {
        cells {
          ...WidgetTable_cell
        }
      }
    `,
    props.row
  );

  return (
    <CellWrapper
      firstColumn={props.firstColumn}
      lastColumn={props.lastColumn}
      header={props.header}
    >
      {row.cells[props.columnIndex] && (
        <Cell cell={row.cells[props.columnIndex]} header={props.header} />
      )}
    </CellWrapper>
  );
};

const Cell = (props: { cell: WidgetTable_cell$key; header?: boolean }) => {
  const cell = useFragment(
    graphql`
      fragment WidgetTable_cell on WidgetTableCell {
        value
        changeType
      }
    `,
    props.cell
  );

  const text = (
    <AHText
      type={props.header || cell.changeType ? "subtitle" : "body"}
      color={cell.changeType ?? "text-primary"}
      numberOfLines={1}
    >
      {cell.value}
    </AHText>
  );

  if (cell.changeType && cell.value) {
    return (
      <Box spaceVertical="xsmall" spaceHorizontal="small">
        <Box
          style={[StyleSheet.absoluteFill, { opacity: 0.1 }]}
          cornerRadius="full"
          color={cell.changeType}
        />
        {text}
      </Box>
    );
  } else {
    return text;
  }
};
